import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../store/useAuth";
import { useEffect, useState } from "react";

// Utility function to handle the QR redirect path
const handleQRRedirectPath = (path: string) => {
  if (!path) return null;
  try {
    if (path.includes("qr-redirect") && path.includes("token=")) {
      // Keep the encoded path as is - don't decode it yet
      return path;
    }
    return path;
  } catch (error) {
    console.error("Error processing redirect path:", error);
    return null;
  }
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isRegistred, validateToken } = useAuth();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkTokenAndRedirect = async () => {
      setIsChecking(true);
      // CRITICAL FIX: Check if we're in the initialization flow or just completed signup
      const appInitialized = localStorage.getItem("appInitialized");
      const signupCompleted = localStorage.getItem("signupCompleted");

      // Don't perform any redirects if we're in initialization flow or just completed signup
      if (appInitialized === "initializing" || signupCompleted === "true") {
        setIsChecking(false);
        return;
      }

      // Check if this is a first-time visit that needs to use get-user flow
      const isFirstTimeVisit = !localStorage.getItem("appVisited");
      if (isFirstTimeVisit) {
        localStorage.setItem("appVisited", "true");
        localStorage.setItem("appInitialized", "initializing");
        navigate("/get-user", { replace: true });
        setIsChecking(false);
        return;
      } // Check if token exists
      const hasValidToken = await validateToken();
      if (!hasValidToken) {
        const currentPath = location.pathname + location.search;

        localStorage.setItem("intendedPath", currentPath);

        if (currentPath.includes("/specializations/")) {
          sessionStorage.setItem("storedSpecializationPath", currentPath);
          sessionStorage.setItem("originalIntendedPath", currentPath);
        }
        if (location.pathname.includes("/specializations/")) {
          const pathParts = location.pathname.split("/");

          if (pathParts.length >= 4) {
            // Store specialization ID for the redirect
            const specializationId = pathParts[2];
            sessionStorage.setItem("pendingSpecialization", specializationId);
            sessionStorage.setItem("qrSpecializationId", specializationId);

            if (pathParts.length >= 6) {
              const facilityId = pathParts[4];
              sessionStorage.setItem("pendingFacility", facilityId);
              sessionStorage.setItem("qrFacilityId", facilityId);
            }
            sessionStorage.setItem("originalIntendedPath", currentPath);
          }
        }
        if (appInitialized !== "initializing" && !isAuthenticated) {
          if (!isRegistred) {
            localStorage.setItem("appInitialized", "initializing");
            navigate("/get-user", {
              state: {
                from: location,
                isNewUser: true,
              },
              replace: true,
            });
          } else {
            // Registered users still go to signin
            navigate("/auth?mode=signin", {
              state: {
                from: location,
                isNewUser: false,
                tokenMissing: true,
              },
              replace: true,
            });
          }
        }

        setIsChecking(false);
        return;
      }

      // Handle redirect if needed
      const handleRedirect = () => {
        const storedPath =
          localStorage.getItem("intendedPath") ||
          sessionStorage.getItem("originalIntendedPath");

        if (storedPath) {
          // Clean up both storage locations
          localStorage.removeItem("intendedPath");
          sessionStorage.removeItem("originalIntendedPath");

          const redirectPath = handleQRRedirectPath(storedPath);

          if (
            redirectPath &&
            location.pathname + location.search !== redirectPath
          ) {
            // Use the path as is, preserving the encoded token
            navigate(redirectPath, { replace: true });
          }
        }
      };

      handleRedirect();
      setIsChecking(false);
    };

    checkTokenAndRedirect();
  }, [isAuthenticated, isRegistred, navigate, location, validateToken]);

  if (isChecking) {
    return null;
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
};
